import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import {
  BYPASS_AUTH_RENEWAL_RESEND,
  BYPASS_SERVER_ERROR_REDIRECT,
  INLINE_SERVER_FIELDS,
  NotificationService,
  SIGNAL_SESSION_RENEWED_NOT_COMPLETED,
  SUPPRESS_SERVICE_UNAVAILABLE_TOAST,
  TOAST_TYPE
} from '../services/notification.service';
import { AuthService } from '../services/auth.service';
import { I18nService } from '../services/i18n.service';
import { LoggerService } from '../services/logger.service';
import { getErrorMessage } from '../mappings/error-mapping';
import { SessionRenewedNotCompletedError, serverFieldError } from '../utils/http-error.util';

/**
 * Fix-loop (code-quality review, US #60394): os códigos de negócio 400 listados abaixo já têm
 * mensagem específica exibida inline pelo componente que fez a chamada — o toast genérico do
 * interceptor deve ficar silencioso para eles, em QUALQUER URL. Substitui as flags booleanas estruturalmente
 * idênticas que existiam aqui antes (uma por código), cada uma repetindo o mesmo par de checks
 * `error.status === 400` + `error.error?.code === X || error.error?.Code === X`.
 *
 * Fix-loop (gate final, Fix 1): `CollectionQueueSolicitation.BarcodeReceiptPermissionDenied` NÃO
 * está mais neste Set — ele precisa de uma restrição de URL (só se aplica ao endpoint de check de
 * código de barras) que este helper, por design, não impõe. Ele continua coberto por
 * `isBarcodeReceiptPermissionDenied`, que agora checa o código diretamente (ver abaixo), sem
 * delegar para este helper "qualquer URL".
 */
const SILENCED_400_CODES_ANY_URL = new Set([
  'SetorHospitalar.DescricaoRequired',
  'SetorHospitalar.DescricaoDuplicada',
  // Identificação vazia/não positiva — exibida inline sob o campo Identificação pelo
  // SetoresHospitalaresModalComponent (INLINE_ERRORS). Ainda se aplica à INCLUSÃO (o WI #62129
  // removeu apenas a capacidade de EDIÇÃO da Identificação).
  'SetorHospitalar.IdentificacaoInvalida',
  // Identificação já usada por outro registro — exibida inline sob o MESMO campo Identificação; o
  // toast genérico repetiria a mensagem idêntica na tela. Idem: ainda se aplica à inclusão.
  'SetorHospitalar.IdentificacaoDuplicada',
  // US 63089: the pre-attendance shows the blocked-patient refusal in its "Paciente bloqueado" dialog.
  'PreAttendance.PatientBlocked',
  // HISTÓRICO — código SUPERADO, não vigente: entre a US #60395 e o WI #62129,
  // `SetorHospitalar.IdentificacaoBloqueadaPorAgrupamento` também entrava aqui (bloqueio, na
  // EDIÇÃO, por vínculo com Agrupamento) — exclusivo do mecanismo "Alterar Identificação" que o
  // WI #62129 removeu por completo (AC-08), junto com o código em si no backend.
]);

/** Resolve se um erro 400 traz um dos códigos acima, em qualquer casing (`code`/`Code`) usado pelo backend, sem restrição de URL. */
const hasSilencedCode = (err: HttpErrorResponse): boolean => {
  const body = err.error as { code?: string; Code?: string } | null;
  return err.status === 400 && !!body && SILENCED_400_CODES_ANY_URL.has(body.code ?? body.Code ?? '');
};

/** Resolve se um erro 400 traz o código BarcodeReceiptPermissionDenied, em qualquer casing (`code`/`Code`). */
const isBarcodeReceiptPermissionDeniedCode = (err: HttpErrorResponse): boolean => {
  const body = err.error as { code?: string; Code?: string } | null;
  return err.status === 400 && !!body &&
    (body.code ?? body.Code ?? '') === 'CollectionQueueSolicitation.BarcodeReceiptPermissionDenied';
};

/**
 * WI #62137 (Spec Q-01): matches `.../setores-hospitalares`, optionally followed by a query string,
 * but NOT `.../setores-hospitalares/{id}` nor `.../setores-hospitalares/{id}/verificar-exclusao` —
 * the `$` anchor (after an optional `?...` with no further `/`) is what excludes `update()`/
 * `delete()`/`verificarExclusao()`, which all have an extra path segment after the resource name.
 */
const SETORES_HOSPITALARES_ANCHORED_PATH_RE = /\/setores-hospitalares(\?[^/]*)?$/;

/**
 * US #62985: `POST .../attendance-center/record-search`, the Central's search by record, with its query string
 * (`?type=`). Anchored on the path, as the Setores Hospitalares one, so no other Central route matches.
 */
const ATTENDANCE_CENTER_RECORD_SEARCH_PATH_RE = /\/attendance-center\/record-search(\?[^/]*)?$/;

/**
 * WI #62143: matches `.../setores-hospitalares/{numeric id}` (EDIT, `update()`), optionally
 * followed by a query string. The `$` anchor excludes `/next-id` (not numeric) and
 * `/{id}/verificar-exclusao` (extra segment).
 */
const SETORES_HOSPITALARES_ID_ANCHORED_PATH_RE = /\/setores-hospitalares\/\d+(\?[^/]*)?$/;

/**
 * WI #62143: a "recognizable" error body is one the format gate below can translate
 * (`code`/`Code`/`message`/`Message`/`errors`). Exported so the Setores Hospitalares modal applies the
 * SAME predicate when deciding whether a 401 is guaranteed to end in the login redirect.
 */
export const hasRecognizableErrorBody = (serverError: unknown): boolean => {
  if (!serverError || typeof serverError !== 'object') return false;
  const body = serverError as Record<string, unknown>;
  return !!(body['code'] || body['Code'] || body['message'] || body['Message'] || body['errors']);
};

/**
 * WI #62143: the session refusal (401 with a recognizable body) of Setores Hospitalares' save
 * calls — INCLUSION (`create()`, POST on the resource) and EDIT (`update()`, PUT `/{id}`).
 * Method + anchored path, same shape as the WI #62137 403 discriminator.
 */
const isSetoresHospitalaresSaveSessionRefusal = (request: HttpRequest<unknown>, error: HttpErrorResponse): boolean =>
  error.status === 401 &&
  hasRecognizableErrorBody(error.error) &&
  ((request.method === 'POST' && SETORES_HOSPITALARES_ANCHORED_PATH_RE.test(error.url ?? '')) ||
    (request.method === 'PUT' && SETORES_HOSPITALARES_ID_ANCHORED_PATH_RE.test(error.url ?? '')));

/** Matches `.../integrations/tasy/hospitals` (create) and nothing below it, optionally followed by a query string. */
const TASY_HOSPITALS_ANCHORED_PATH_RE = /\/integrations\/tasy\/hospitals(\?[^/]*)?$/;

/** Matches `.../integrations/tasy/hospitals/{numeric id}` (edit); `/{id}/active` and the other sub-routes stay out. */
const TASY_HOSPITALS_ID_ANCHORED_PATH_RE = /\/integrations\/tasy\/hospitals\/\d+(\?[^/]*)?$/;

/** The Tasy hospital ficha's save — create (POST) and edit (PUT `/{id}`), by method + anchored path. */
const isTasyHospitalSave = (request: HttpRequest<unknown>, error: HttpErrorResponse): boolean =>
  (request.method === 'POST' && TASY_HOSPITALS_ANCHORED_PATH_RE.test(error.url ?? '')) ||
  (request.method === 'PUT' && TASY_HOSPITALS_ID_ANCHORED_PATH_RE.test(error.url ?? ''));

/**
 * A 401 with a recognizable body on the Tasy hospital ficha's save. The ficha leaves every 401 to this
 * interceptor and shows no toast of its own for it, and the backend's authentication challenge always
 * answers a 401 with a `code`/`message` body, which the format gate below would otherwise turn into a
 * silent outcome under the ficha's `info` toast type.
 */
const isTasyHospitalSaveSessionRefusal = (request: HttpRequest<unknown>, error: HttpErrorResponse): boolean =>
  error.status === 401 && hasRecognizableErrorBody(error.error) && isTasyHospitalSave(request, error);

/**
 * How `handle401Error` treats the renewal outcome:
 * - `'platform'`: every session refusal except the Tasy hospital save.
 * - `'tasy'`: the Tasy hospital save, with and without a body, whose renewal outcome is kept exactly as
 *   it is — the renewal's `true` is trusted, the resend has no handling of
 *   its own and a renewal that does not renew also shows the `error` toast.
 */
type SessionRenewalMode = 'platform' | 'tasy';

/**
 * The credential the session holds now, in the priority the AuthInterceptor uses to build the
 * `Authorization` header — the one a resend would carry.
 */
const currentCredential = (authService: AuthService): string | null =>
  authService.getIdToken() || authService.getAccessToken() || authService.getToken() || null;

/** The credential the refused request carried in `Authorization: Bearer …`, or `null` when it carried none. */
const refusedCredential = (request: HttpRequest<unknown>): string | null => {
  const match = /^Bearer\s+(.+)$/i.exec(request.headers.get('Authorization') ?? '');
  return match ? match[1] : null;
};

/** Logged once on each of the two resend paths (`platformRenewalOutcome`, `tasyRenewalOutcome`). */
const RESEND_AFTER_RENEWAL_LOG = 'HttpErrorInterceptor - Reenviando requisição após refresh bem-sucedido.';

/** The single resend of a refused request: its headers, the renewed credential and the `X-Auth-Retry` mark. */
const withRenewedCredential = (request: HttpRequest<unknown>, credential: string): HttpRequest<unknown> =>
  request.clone({
    setHeaders: {
      ...request.headers.keys().reduce((acc, key) => {
        const value = request.headers.get(key);
        if (value !== null) {
          acc[key] = value;
        }
        return acc;
      }, {} as { [key: string]: string }),
      Authorization: `Bearer ${credential}`,
      'X-Auth-Retry': 'true'
    }
  });

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private redirectingToServerError = false;
  private conductingToLogin = false;

  constructor(
    private injector: Injector,
    private router: Router
  ) {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        if (!e.urlAfterRedirects.includes('/internal-server-error')) {
          this.redirectingToServerError = false;
        }
        if (!e.urlAfterRedirects.includes('/login')) {
          this.conductingToLogin = false;
        }
      });
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        const toastType = request.context.get(TOAST_TYPE);
        const notificationService = this.injector.get(NotificationService);
        const authService = this.injector.get(AuthService);
        const i18n = this.injector.get(I18nService);
 
        const forceLogoutReason = error.headers?.get('X-Autolac-Force-Logout');
        if (forceLogoutReason) {
          console.warn('HttpErrorInterceptor - Force logout requested by server:', {
            reason: forceLogoutReason,
            url: error.url,
            status: error.status
          });
          authService.clearSessionAndRedirectToLogin({ sessionExpired: true });
          return throwError(() => error);
        }

        /**
         * WI #62143 (PO decision, Spec §6): a 401 with a recognizable body on the Setores
         * Hospitalares save calls would otherwise skip the whole `switch (error.status)` below
         * (format gate) and leave the user on the form with an `error` toast. End the session and
         * lead to the login WITHOUT a toast here: the "session expired" warning is emitted by the
         * login page itself (login-page.component.ts), so the user sees exactly one. Scoped: a 401
         * WITH a body on any other call stays with the format gate.
         */
        if (isSetoresHospitalaresSaveSessionRefusal(request, error)) {
          authService.clearSessionAndRedirectToLogin({ sessionExpired: true });
          return throwError(() => error);
        }

        // The same path as the save's bodyless 401 (`handle401Error` in mode 'tasy'): renew the
        // credential and, failing that, lead to the login. The resend leaves through `next.handle`,
        // below this `catchError`, so its refusal is caught here: a 401 on the resend is a session the
        // renewal did not save, and it ends at the login, whose page announces the expired session.
        // No second resend.
        if (isTasyHospitalSaveSessionRefusal(request, error)) {
          const handled401$ = this.handle401Error(error, request, next, authService, notificationService, {
            title: i18n.translate('notifications.unauthorized'),
            message: i18n.translate('notifications.sessionExpired')
          }, 'tasy');
          return (handled401$ ?? throwError(() => error)).pipe(
            catchError((resendError: unknown) => {
              if (resendError !== error && resendError instanceof HttpErrorResponse && resendError.status === 401) {
                authService.clearSessionAndRedirectToLogin({ sessionExpired: true });
              }
              return throwError(() => resendError);
            })
          );
        }

        let errorInfo = { 
          title: i18n.translate('errors.defaultTitle'), 
          message: i18n.translate('errors.defaultMessage') 
        };

        if (error.error instanceof ErrorEvent) {
          errorInfo = {
            title: i18n.translate('notifications.connectionError'),
            message: error.error.message || i18n.translate('notifications.networkError')
          };
        } else {
          const serverError = error.error;

          if (hasRecognizableErrorBody(serverError)) {
            const mappedError = getErrorMessage(serverError, undefined, i18n);
            errorInfo = {
              title: mappedError.title,
              message: mappedError.message
            };
          } else {
            switch (error.status) {
              case 400:
                errorInfo = {
                  title: i18n.translate('notifications.invalidRequest'),
                  message: i18n.translate('notifications.invalidRequestMessage')
                };
                break;
              case 401: {
                errorInfo = {
                  title: i18n.translate('notifications.unauthorized'),
                  message: i18n.translate('notifications.sessionExpired')
                };

                const handled401$ = this.handle401Error(
                  error,
                  request,
                  next,
                  authService,
                  notificationService,
                  errorInfo,
                  isTasyHospitalSave(request, error) ? 'tasy' : 'platform'
                );

                if (handled401$) {
                  return handled401$;
                }

                break;
              }
              case 403:
                errorInfo = {
                  title: i18n.translate('notifications.accessDenied'),
                  message: i18n.translate('notifications.accessDeniedMessage')
                };
                break;
              case 404:
                errorInfo = {
                  title: i18n.translate('notifications.notFound'),
                  message: i18n.translate('notifications.notFoundMessage')
                };
                break;
              case 422:
                errorInfo = {
                  title: i18n.translate('notifications.validationError'),
                  message: i18n.translate('notifications.validationErrorMessage')
                };
                break;
              case 429:
                errorInfo = {
                  title: i18n.translate('notifications.tooManyRequests'),
                  message: i18n.translate('notifications.tooManyRequestsMessage')
                };
                break;
              case 500:
                errorInfo = {
                  title: i18n.translate('notifications.serverError'),
                  message: i18n.translate('notifications.serverErrorMessage')
                };
                break;
              case 503:
                errorInfo = {
                  title: i18n.translate('notifications.serviceUnavailable'),
                  message: i18n.translate('notifications.serviceUnavailableMessage')
                };
                break;
              default:
                errorInfo = {
                  title: i18n.translate('errors.defaultTitle'),
                  message: `${i18n.translate('errors.defaultTitle')} ${error.status}: ${error.statusText || i18n.translate('errors.defaultMessage')}`
                };
            }
          }
        }
        
        /** Foto de perfil ausente / não encontrada no Graph — não mostrar toast nem erro técnico no console. */
        const isPhotoMissing =
          error.url != null &&
          error.url.includes('/users/me/photo') &&
          (error.status === 400 || error.status === 404);
        /** GET acessos rápidos: falha comum (ex. utilizador ainda sem mapeamento) — home usa lista vazia; POST de guardar mantém toast se falhar. */
        const isQuickAccessGetBenign =
          request.method === 'GET' &&
          (request.url.includes('/user/quick-access') || (error.url != null && error.url.includes('/user/quick-access'))) &&
          error.status >= 400 &&
          error.status < 500;
        /** Unknown barcode: handler returns 400 + BarcodeNotFound (404 kept per declared endpoint contract); the component shows the code-specific message. */
        const isBarcodeCheckNotFoundError =
          error.url != null &&
          error.url.includes('collection-queue-barcodes/check') &&
          (
            error.status === 404 ||
            (error.error as { code?: string; Code?: string } | null)?.code === 'CollectionQueueSolicitation.BarcodeNotFound' ||
            (error.error as { code?: string; Code?: string } | null)?.Code === 'CollectionQueueSolicitation.BarcodeNotFound'
          );
        /**
         * 400 com BarcodeReceiptPermissionDenied: componente mostra mensagem específica — não exibir
         * toast genérico de validação. Restrito à URL do check de código de barras: o código sozinho
         * NÃO basta (fix-loop, gate final, Fix 1) — este é o único ponto que decide esse código, via
         * `isBarcodeReceiptPermissionDeniedCode`, não mais via `hasSilencedCode` (que cobre apenas
         * os códigos de `SILENCED_400_CODES_ANY_URL`, todos sem restrição de URL).
         */
        const isBarcodeReceiptPermissionDenied =
          error.url != null &&
          error.url.includes('collection-queue-barcodes/check') &&
          isBarcodeReceiptPermissionDeniedCode(error);
        /**
         * WI #62137 (AC-01/AC-02): a 403 on Setores Hospitalares' LIST (`search()`, GET) or
         * INCLUSION (`create()`, POST) must come out as a `warning` toast, not the `error` default
         * `TOAST_TYPE` produces — the design-system contract fixes `warning` for status 403
         * (ds-contract.json, mensagensDeRetorno.porStatus."403"). Anchored on the PATH (via
         * `SETORES_HOSPITALARES_ANCHORED_PATH_RE`, ignoring an optional query string) so it does
         * NOT also match `update()`/`delete()`/`verificarExclusao()` — those three share the same
         * `setores-hospitalares` substring but always have an extra `/{id}` (and, for
         * `verificarExclusao`, `/verificar-exclusao`) path segment after it, which the regex's `$`
         * anchor excludes. The HTTP method check is a second, redundant layer (Spec Q-01): the path
         * anchor alone already excludes the other three calls, but the method keeps the
         * discriminator robust to a future GET/POST landing on this exact path.
         */
        const isSetoresHospitalaresSearchOrCreateAccessDenied =
          error.status === 403 &&
          (request.method === 'GET' || request.method === 'POST') &&
          SETORES_HOSPITALARES_ANCHORED_PATH_RE.test(error.url ?? '');
        /**
         * US #62985 (AC5, E2, plan OQ-4 A): the search by budget without routine 50 is refused with 403 { code, message }.
         * The Central marks its calls TOAST_TYPE 'info' (the screen owns its errors), which would keep this refusal
         * silent; the design-system contract fixes `warning` for 403 (ds-contract.json,
         * mensagensDeRetorno.porStatus."403"), with the server's `message` and the title of permission. Only this POST
         * and only 403: the screen keeps owning every other answer of the Central.
         */
        const isAttendanceCenterRecordSearchAccessDenied =
          error.status === 403 &&
          request.method === 'POST' &&
          ATTENDANCE_CENTER_RECORD_SEARCH_PATH_RE.test(error.url ?? '');
        /** validate-token: fluxo SSO trata erro (modal / mensagem amigável); evitar toast técnico do interceptor. */
        const isValidateToken = error.url != null && error.url.includes('/auth/validate-token');
        /** Primeiro acesso via Web: toasts tratados no modal (evita duplicar mensagem genérica do servidor). */
        const isFirstAccessEndpoint =
          (error.url != null && error.url.includes('first-access')) ||
          request.url.includes('first-access');
        /** POST usage telemetry (fire-and-forget): failure must never redirect the app. */
        const isUsageTelemetry = request.url.includes('/usage-log');
        /**
         * Opt-in per-request flag (spec §3): the caller owns the 5xx/503 contract and must
         * stay on the current screen (e.g. the health-facility ficha keeps its data on server
         * error), so skip the global /internal-server-error redirect and let the error
         * propagate to the caller. Default false → every other request still redirects.
         */
        const bypassServerErrorRedirect = request.context.get(BYPASS_SERVER_ERROR_REDIRECT);
        /**
         * WI #62157 (decision of the PO, 2026-10-01): opt-in, off by default. When the caller asks and
         * the response status is exactly 503, the generic toast below is skipped because the caller
         * shows a persistent banner instead. The cut is by STATUS, never by the body, and nothing else
         * in this flow changes (log, redirect, text mapping, 403/429/patient/requester branches). The
         * structural fix (variant per status) is the future US #61748.
         */
        const suppressServiceUnavailableToast =
          error.status === 503 && request.context.get(SUPPRESS_SERVICE_UNAVAILABLE_TOAST);
        /** WI #62581: the caller shows this refusal under its field, so the toast would repeat it. */
        const isShownOnField = serverFieldError(error, request.context.get(INLINE_SERVER_FIELDS)) !== null;
        if (!error.url?.includes('/auth/') && !isPhotoMissing && !isQuickAccessGetBenign && !isBarcodeCheckNotFoundError && !isBarcodeReceiptPermissionDenied) {
          console.error('HTTP Error:', {
            url: error.url,
            status: error.status,
            title: errorInfo.title,
            message: errorInfo.message,
            error: error.error
          });
        }
 
        if (error.status >= 500 && !isUsageTelemetry && !bypassServerErrorRedirect) {
          const alreadyThere = this.router.url.includes('/internal-server-error');
          if (!alreadyThere && !this.redirectingToServerError) {
            this.redirectingToServerError = true;
            this.router.navigate(['/internal-server-error']);
          }
          return throwError(() => error);
        }

        const isAuthEndpoint401 = error.status === 401 && error.url?.includes('/auth/');
        if (
          !isAuthEndpoint401 &&
          !isPhotoMissing &&
          !isQuickAccessGetBenign &&
          !isBarcodeCheckNotFoundError &&
          !isBarcodeReceiptPermissionDenied &&
          !hasSilencedCode(error) &&
          !isValidateToken &&
          !isFirstAccessEndpoint &&
          !suppressServiceUnavailableToast &&
          !isShownOnField
        ) {
          // 429: many parallel calls (dashboard, Fila de Coleta) hit rate limit together — one toast only.
          // collapseGroup lets ToastComponent replace duplicates in the same tick (showOnce window alone can race).
          const serverBody = error.error && typeof error.error === 'object' ? error.error as { code?: string; Code?: string } : null;
          const isRateLimited =
            error.status === 429 ||
            serverBody?.code === 'RateLimit.Exceeded' ||
            serverBody?.Code === 'RateLimit.Exceeded';
          const isPatientAccessDenied =
            serverBody?.code === 'Paciente.AcessoNegado' ||
            serverBody?.Code === 'Paciente.AcessoNegado';
          const isPatientEditAccessDenied =
            serverBody?.code === 'Paciente.AlteracaoNegada' ||
            serverBody?.Code === 'Paciente.AlteracaoNegada';
          const isPatientIdentificationEditAccessDenied =
            serverBody?.code === 'Paciente.AlteracaoIdentificacaoNegada' ||
            serverBody?.Code === 'Paciente.AlteracaoIdentificacaoNegada';
          const isRequesterAccessDenied =
            serverBody?.code === 'Solicitante.AcessoNegado' ||
            serverBody?.Code === 'Solicitante.AcessoNegado';
          const isRequesterEditAccessDenied =
            serverBody?.code === 'Solicitante.AlteracaoNegada' ||
            serverBody?.Code === 'Solicitante.AlteracaoNegada';

          if (isPatientAccessDenied) {
            // Translate here, not via errorInfo: ERROR_MAPPINGS resolves before I18nService exists.
            notificationService.warning(
              i18n.translate('patient.errors.accessDenied'),
              i18n.translate('notifications.accessDenied')
            );
          } else if (isPatientEditAccessDenied) {
            // Same reasoning as isPatientAccessDenied above.
            notificationService.warning(
              i18n.translate('patient.errors.editAccessDenied'),
              i18n.translate('notifications.accessDenied')
            );
          } else if (isPatientIdentificationEditAccessDenied) {
            // Same reasoning as isPatientAccessDenied above.
            notificationService.warning(
              i18n.translate('patient.errors.identificationEditAccessDenied'),
              i18n.translate('notifications.accessDenied')
            );
          } else if (isRequesterAccessDenied) {
            // Same reasoning as isPatientAccessDenied above.
            notificationService.warning(
              i18n.translate('requester.errors.accessDenied'),
              i18n.translate('notifications.accessDenied')
            );
          } else if (isAttendanceCenterRecordSearchAccessDenied) {
            // US #62985: the variant AND the title change (the recognizable body would title it "Erro"); the text is
            // the server's message, as mapped above.
            notificationService.warning(errorInfo.message, i18n.translate('notifications.accessDenied'));
          } else if (isSetoresHospitalaresSearchOrCreateAccessDenied) {
            // WI #62137 (AC-01/AC-02): only the toast VARIANT changes here (to `warning`) — never
            // the text. errorInfo.title/message is whatever was already resolved above: either the
            // mapped server message via `getErrorMessage` when the body carries a code/message
            // (the common case, and what AC-01/AC-02's own tests exercise), or, when the body
            // carries neither, the generic case-403 defaults (`notifications.accessDenied` /
            // `notifications.accessDeniedMessage`).
            notificationService.warning(errorInfo.message, errorInfo.title);
          } else if (isRequesterEditAccessDenied) {
            // Same reasoning as isPatientAccessDenied above.
            notificationService.warning(
              i18n.translate('requester.errors.editAccessDenied'),
              i18n.translate('notifications.accessDenied')
            );
          } else if (isRateLimited) {
            notificationService.showOnce(
              'http:429',
              {
                type: 'warning',
                message: errorInfo.message,
                title: errorInfo.title,
                duration: 7000,
                collapseGroup: 'rate-limit'
              },
              15_000
            );
          } else {
            switch (toastType) {
              case 'error':
                notificationService.error(errorInfo.message, errorInfo.title);
                break;
              case 'warning':
                notificationService.warning(errorInfo.message, errorInfo.title);
                break;
              default:
                break;
            }
          }
        }

        return throwError(() => error);
      })
    );
  }

  private handle401Error(
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
    next: HttpHandler,
    authService: AuthService,
    notificationService: NotificationService,
    errorInfo: { title: string; message: string },
    mode: SessionRenewalMode
  ): Observable<HttpEvent<unknown>> | null {
    const currentUrl = this.router.url;
    const isAuthEndpoint = error.url?.includes('/auth/');
    const isLoginPage = currentUrl.includes('/login');
    const alreadyRetried = request.headers.has('X-Auth-Retry');

    console.log('🔍 HttpErrorInterceptor - 401 detectado:', {
      url: error.url,
      currentUrl: currentUrl,
      isAuthEndpoint: isAuthEndpoint,
      isLoginPage: isLoginPage,
      alreadyRetried
    });

    if (!isLoginPage && !isAuthEndpoint && !alreadyRetried) {
      console.log('🔄 HttpErrorInterceptor - Tentando refresh de token antes de forçar logout.');
      return authService.refreshAccessToken().pipe(
        catchError(refreshError => {
          console.error('HttpErrorInterceptor - Erro ao tentar refresh após 401:', refreshError);
          return of(false);
        }),
        switchMap((success) => {
          // Opt-out of the RESEND only (US #61006, Cenário 10a). The credential has just been
          // renewed and stays renewed; what this skips is re-executing the original request on
          // the user's behalf. The resend was designed for a read — on a destructive write it
          // performs an operation nobody asked for twice, and the second one reports that
          // somebody else did what the user did. Default false, so no request that does not
          // declare the token changes behaviour. Without SIGNAL_SESSION_RENEWED_NOT_COMPLETED, a
          // renewal `true` hands the original 401 back to the caller with no credential comparison
          // and no conduction, the screen deciding the outcome; a renewal that does not renew still
          // leads to the login below. WITH the signal, the request goes through the platform's
          // credential comparison (`platformRenewalOutcome`).
          if (
            success &&
            request.context.get(BYPASS_AUTH_RENEWAL_RESEND) &&
            !request.context.get(SIGNAL_SESSION_RENEWED_NOT_COMPLETED)
          ) {
            return throwError(() => error);
          }

          return mode === 'tasy'
            ? this.tasyRenewalOutcome(success, error, request, next, authService, notificationService, errorInfo)
            : this.platformRenewalOutcome(success, error, request, next, authService);
        })
      );
    }

    if (!isLoginPage && !isAuthEndpoint) {
      console.warn('⚠️ HttpErrorInterceptor - 401 em endpoint protegido após tentativa de refresh (ou sem refresh), limpando sessão e redirecionando para login.');
      authService.clearSessionAndRedirectToLogin({ sessionExpired: true });
    } else {
      console.log('⏭️ HttpErrorInterceptor - 401 em endpoint de auth ou login, não limpando tokens');
    }

    return null;
  }

  /**
   * The outcome of the renewal for a session refusal (a 401 without a recognizable body) on every
   * request but the Tasy hospital save:
   * - renewal OBTAINED = the renewal answered `true` AND the session now holds a credential that is
   *   non-empty and different from the one the refused request carried. A 2xx that stored nothing
   *   (`AuthService.refreshAccessToken` maps any 2xx to `true`), or one that only replaced a credential
   *   ranked behind the one sent, leaves the refused credential in front, and a resend would carry it
   *   back to the server that has just refused it. Deciding it HERE keeps the `AuthService` as it is
   *   for its other consumers — the features' own workarounds for a 401 with a body, and the periodic
   *   renewal. A request that carried no `Bearer` only needs a non-empty credential now;
   * - obtained, on a request that declares BYPASS_AUTH_RENEWAL_RESEND AND SIGNAL_SESSION_RENEWED_NOT_COMPLETED
   *   (the health-facility writes) → no resend and no conduction; the request fails with a
   *   `SessionRenewedNotCompletedError` carrying the original 401, and the screen asks for a new
   *   confirmation;
   * - obtained, otherwise → one resend with the new credential. A resend refused again by session (401 without a
   *   recognizable body, a `Blob` included) leads to the login, with no second renewal; any other
   *   failure of the resend reaches the caller as it is;
   * - not obtained → no resend; lead to the login.
   *
   * No toast on any of these, under any `TOAST_TYPE`: the login page's warning is the one voice of the
   * expired session (`ds-contract.json`, `porStatus.401`). The refusal still reaches the caller.
   */
  private platformRenewalOutcome(
    success: boolean,
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
    next: HttpHandler,
    authService: AuthService
  ): Observable<HttpEvent<unknown>> {
    const logger = this.injector.get(LoggerService);
    const credential = success ? currentCredential(authService) : null;

    if (!credential || credential === refusedCredential(request)) {
      logger.warn('HttpErrorInterceptor - Renovação não obtida (recusada, falhou ou sem credencial nova), conduzindo ao login.');
      this.conductToLogin(authService, request);
      return throwError(() => error);
    }

    if (request.context.get(BYPASS_AUTH_RENEWAL_RESEND) && request.context.get(SIGNAL_SESSION_RENEWED_NOT_COMPLETED)) {
      logger.warn('HttpErrorInterceptor - Renovação obtida; a requisição pediu para não ser reenviada e recebe o sinal de operação não concluída.');
      return throwError(() => new SessionRenewedNotCompletedError(error));
    }

    const retryRequest = withRenewedCredential(request, credential);
    logger.log(RESEND_AFTER_RENEWAL_LOG);
    return next.handle(retryRequest).pipe(
      catchError((resendError: unknown) => {
        if (
          resendError instanceof HttpErrorResponse &&
          resendError.status === 401 &&
          !hasRecognizableErrorBody(resendError.error)
        ) {
          this.conductToLogin(authService, retryRequest);
        }
        return throwError(() => resendError);
      })
    );
  }

  /**
   * The outcome of the renewal for the Tasy hospital save, with and without a body: a renewal `true`
   * is trusted, and the resend carries whatever credential the session holds; a resend refused again has no handling here (the caller of the Tasy
   * branch in `intercept` adds its own for a save with a body); a renewal `true` with no credential at
   * all only rethrows; and a renewal that does not renew leads to the login AND shows the `error` toast.
   */
  private tasyRenewalOutcome(
    success: boolean,
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
    next: HttpHandler,
    authService: AuthService,
    notificationService: NotificationService,
    errorInfo: { title: string; message: string }
  ): Observable<HttpEvent<unknown>> {
    const logger = this.injector.get(LoggerService);

    if (success) {
      const newToken = currentCredential(authService);

      if (!newToken) {
        logger.warn('HttpErrorInterceptor - Refresh retornou sucesso, mas nenhum token novo foi encontrado.');
        return throwError(() => error);
      }

      logger.log(RESEND_AFTER_RENEWAL_LOG);
      return next.handle(withRenewedCredential(request, newToken));
    }

    logger.warn('HttpErrorInterceptor - Refresh não conseguiu renovar o token, limpando sessão e redirecionando para login.');
    authService.clearSessionAndRedirectToLogin({ sessionExpired: true });
    notificationService.error(errorInfo.message, errorInfo.title);
    return throwError(() => error);
  }

  /**
   * Leads to the login — `clearSessionAndRedirectToLogin({ sessionExpired: true })`, with no toast — at
   * most ONCE per refusal episode. It does not lead there when:
   * (a) this episode already did. Simultaneous refusals share one renewal (`refreshAccessToken` hands
   *     every caller the in-flight observable) and receive the same outcome, and so can resends refused
   *     side by side. The guard rearms on the first `NavigationEnd` outside `/login`, i.e. after the new
   *     login;
   * (b) the refused request carried a credential and the session holds none any more: the session was
   *     ended DURING the episode by whoever has already led to the login — the `AuthService` itself,
   *     when `/auth/refresh` answers 401, clears the session and navigates before handing `false` over.
   * A request that left WITHOUT a credential (the session had been cleared locally without navigating
   * before the call) is led to the login. Accepted residual risk: a session cleared locally without
   * navigating while the request was in flight reads as (b), and that refusal is not led to the login.
   */
  private conductToLogin(authService: AuthService, request: HttpRequest<unknown>): void {
    if (this.conductingToLogin) {
      return;
    }
    if (refusedCredential(request) && !currentCredential(authService)) {
      return;
    }
    this.conductingToLogin = true;
    authService.clearSessionAndRedirectToLogin({ sessionExpired: true });
  }
}
